import * as React from "react";
import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";

/**
 * A view in the side panel stack.
 */
export interface SidePanelView {
  /** Unique identifier for the view */
  id: string;
  /** Title displayed in the header */
  title: string;
  /** The content to render */
  content: React.ReactNode;
}

interface SidePanelStackContextValue {
  /** Current view stack */
  stack: SidePanelView[];
  /** Push a new view onto the stack */
  push: (view: SidePanelView) => void;
  /** Pop the current view and go back */
  pop: () => void;
  /** Replace the entire stack with a single view */
  reset: (view: SidePanelView) => void;
  /** Current view (top of stack) */
  currentView: SidePanelView | null;
  /** Whether we can go back (stack has more than 1 view) */
  canGoBack: boolean;
  /** Direction of last navigation for animation */
  direction: "forward" | "back";
}

const SidePanelStackContext = createContext<SidePanelStackContextValue | null>(
  null
);

/**
 * Hook to access the side panel stack context.
 * Must be used within a SidePanelStack provider.
 */
export function useSidePanelStack() {
  const context = useContext(SidePanelStackContext);
  if (!context) {
    throw new Error("useSidePanelStack must be used within a SidePanelStack");
  }
  return context;
}

/**
 * Optional variant — returns null when rendered outside a SidePanelStack.
 * Useful for components shared between side-panel and standalone contexts.
 */
export function useSidePanelStackOptional() {
  return useContext(SidePanelStackContext);
}

interface SidePanelStackProps {
  /** Initial view to display */
  initialView: SidePanelView;
  /** Called when the stack is reset (e.g., panel closed) */
  onReset?: () => void;
  /** Children that will have access to the stack context */
  children: (props: {
    currentView: SidePanelView;
    canGoBack: boolean;
    onBack: () => void;
  }) => React.ReactNode;
}

/**
 * SidePanelStack - Manages a stack of views within a side panel with animated transitions.
 *
 * Usage:
 * ```tsx
 * <SidePanelStack initialView={{ id: "main", title: "Info", content: <MainView /> }}>
 *   {({ currentView, canGoBack, onBack }) => (
 *     <>
 *       <SidePanelHeader
 *         title={currentView.title}
 *         onBack={canGoBack ? onBack : undefined}
 *         onClose={...}
 *       />
 *       <SidePanelStackContent />
 *     </>
 *   )}
 * </SidePanelStack>
 * ```
 */
export function SidePanelStack({
  initialView,
  onReset,
  children,
}: SidePanelStackProps) {
  // Combined state ensures direction and stack update atomically in a single
  // setState call.  Two separate states can cause AnimatePresence to miss the
  // enter animation when direction is already "forward" (a no-op setState).
  const [{ stack, direction }, setStackState] = useState<{
    stack: SidePanelView[];
    direction: "forward" | "back";
  }>({ stack: [initialView], direction: "forward" });

  // Keep the root view in sync with initialView when data loads
  // Only update if we're still on the root view (stack length === 1)
  useEffect(() => {
    setStackState((prev) => {
      if (prev.stack.length === 1 && prev.stack[0].id === initialView.id) {
        return { ...prev, stack: [initialView] };
      }
      return prev;
    });
  }, [initialView]);

  const push = useCallback((view: SidePanelView) => {
    setStackState((prev) => ({
      direction: "forward",
      stack: [...prev.stack, view],
    }));
  }, []);

  const pop = useCallback(() => {
    setStackState((prev) => ({
      direction: "back",
      stack: prev.stack.length <= 1 ? prev.stack : prev.stack.slice(0, -1),
    }));
  }, []);

  const reset = useCallback(
    (view: SidePanelView) => {
      setStackState({ direction: "forward", stack: [view] });
      onReset?.();
    },
    [onReset]
  );

  const currentView = stack[stack.length - 1] ?? null;
  const canGoBack = stack.length > 1;

  const value = useMemo(
    () => ({
      stack,
      push,
      pop,
      reset,
      currentView,
      canGoBack,
      direction,
    }),
    [stack, push, pop, reset, currentView, canGoBack, direction]
  );

  if (!currentView) return null;

  return (
    <SidePanelStackContext.Provider value={value}>
      {children({ currentView, canGoBack, onBack: pop })}
    </SidePanelStackContext.Provider>
  );
}

// Variants as functions that receive direction via custom prop
// This ensures both entering and exiting elements use the same direction
const slideVariants = {
  enter: (direction: "forward" | "back") => ({
    x: direction === "forward" ? "100%" : "-100%",
  }),
  center: {
    x: 0,
  },
  exit: (direction: "forward" | "back") => ({
    x: direction === "forward" ? "-100%" : "100%",
  }),
};

/**
 * SidePanelStackContent - Renders the current view with animated transitions.
 * Must be used within a SidePanelStack.
 *
 * Animation behavior:
 * - Push (forward): Both screens slide LEFT (new from right, old to left)
 * - Pop (back): Both screens slide RIGHT (new from left, old to right)
 */
export function SidePanelStackContent({
  className,
}: {
  className?: string;
}) {
  const { currentView, direction } = useSidePanelStack();
  const isInitialMount = useRef(true);

  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  if (!currentView) return null;

  return (
    <div className={className} style={{ position: "relative", overflow: "hidden", flex: 1, display: "flex", flexDirection: "column" }}>
      <AnimatePresence custom={direction}>
        <motion.div
          key={currentView.id}
          custom={direction}
          initial={isInitialMount.current ? false : "enter"}
          animate="center"
          exit="exit"
          variants={slideVariants}
          transition={{
            type: "tween",
            duration: 0.3,
            ease: [0.32, 0.72, 0, 1], // iOS-style ease curve
          }}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {currentView.content}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
